const UNLIMITED = -1;

export interface SummaryBand { name: string; price: number; active?: boolean | null }
export interface SummaryTier {
  name: string;
  price: number;
  availableSpaces?: number | null;
  showSeats?: boolean | null;
  prices?: SummaryBand[] | null;
}

/**
 * Every purchasable price band across an event's tiers.
 *
 * Falls back to a tier's own flat price only when it has no bands — events
 * created before bands existed must still show a price.
 */
export const allBands = (tiers: SummaryTier[] = []): SummaryBand[] => tiers.flatMap((tier) => {
  const active = (tier.prices || []).filter((band) => band.active !== false);
  return active.length ? active : [{ name: tier.name, price: tier.price }];
});

/**
 * Cheapest price a customer could actually pay, in pence.
 *
 * The listing previously read `tier.price` — the DEPRECATED flat price — so an
 * event selling Adult £25 / Child £15 advertised "From £25". The cheapest real
 * band is what "from" means.
 */
export const lowestPrice = (tiers: SummaryTier[] = []): number | null => {
  const prices = allBands(tiers).map((band) => band.price).filter((p) => typeof p === 'number');
  return prices.length ? Math.min(...prices) : null;
};

/**
 * Seats left across an event, or null when any tier is unlimited.
 *
 * Summing raw `availableSpaces` was wrong: unlimited tiers carry -1, so an
 * unlimited tier plus a tier with one seat summed to 0 and the event read
 * "Sold out" while seats were on sale.
 */
export const seatsRemaining = (tiers: SummaryTier[] = []): number | null => {
  if (tiers.some((tier) => tier.availableSpaces === UNLIMITED)) return null;
  return tiers.reduce((total, tier) => total + (tier.availableSpaces ?? 0), 0);
};

/** Sold out only when every tier is finite and none has a seat left. */
export const isSoldOut = (tiers: SummaryTier[] = []): boolean => {
  if (!tiers.length) return false;
  const remaining = seatsRemaining(tiers);
  return remaining !== null && remaining === 0;
};

/** Show a scarcity note only when it is true and worth saying. */
export const LOW_STOCK_THRESHOLD = 10;

export const availabilityNote = (tiers: SummaryTier[] = []): string | null => {
  if (isSoldOut(tiers)) return 'Sold out';
  const remaining = seatsRemaining(tiers);
  if (remaining === null || remaining > LOW_STOCK_THRESHOLD) return null;
  if (!tiers.some((tier) => tier.showSeats)) return null;
  return remaining === 1 ? 'Last seat' : `Only ${remaining} left`;
};

/**
 * How a card should summarise price.
 *
 * With one tier, naming the bands is genuinely useful — "Adult £25 · Child
 * £15" tells a parent what the evening costs. Across several tiers the same
 * band names repeat at different prices ("Adult £40, Child £25, Adult £25,
 * Child £15"), which reads as a mistake, so the card falls back to the lowest
 * price and leaves the breakdown to the event page.
 */
export const priceSummary = (tiers: SummaryTier[] = []): {
  kind: 'bands' | 'from' | 'none'; bands: SummaryBand[]; from: number | null;
} => {
  const from = lowestPrice(tiers);
  if (from === null) return { kind: 'none', bands: [], from: null };
  if (tiers.length === 1) return { kind: 'bands', bands: allBands(tiers), from };
  return { kind: 'from', bands: [], from };
};
